import { hasContextualFees } from '@openx/components-targeting/Targeting/ContextualSegments/utils/contextualPricing';
import { type DealFormDataWithTargeting, dealFormField } from '@openx/types/deal';
import { SegmentPartnerRevenueMethod } from '@openx/types/gqlTypes';
import type { AudiencePricing } from '@openx/types/targeting/audience';
import { MatchingOperator } from '@openx/types/targeting/audience';
import { usePrevious } from '@openx/utils/hooks/usePrevious';
import isEqual from 'lodash/isEqual';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
import { CONTEXTUAL_PLATFORM_FEE_SUPPORTED } from '../components/EnableDataPartners/constants';
import { useDealFormContext } from '../DealForm/DealForm.context';
import { useDealContext } from '../utils/context/DealContext';
import { type ContextualPricingState, NO_CONTEXTUAL_PRICING } from './useContextualPricingState';
import { useIntersectingAudienceCpmLock } from './useIntersectingAudienceCpmLock';

type UseDataPartnerFeeLogicProps = {
  pricingInfo?: AudiencePricing;
  hasMultiProviderPricingData: boolean;
  loadingAudienceCheck: boolean;
  contextualPricing?: ContextualPricingState;
};

type DataPartnerFeeLogicResult = {
  disableRadio: boolean;
  infoMessage: string | null;
};

export const useDataPartnerFeeLogic = ({
  pricingInfo,
  hasMultiProviderPricingData,
  loadingAudienceCheck,
  contextualPricing: { pricing: contextualPricing, isLoading, isFailed } = NO_CONTEXTUAL_PRICING,
}: UseDataPartnerFeeLogicProps): DataPartnerFeeLogicResult => {
  const { setValue, getValues, trigger } = useFormContext<DealFormDataWithTargeting>();
  const { dealUid } = useParams();
  const { pckgIsInitialLoading } = useDealFormContext();
  const skippedWhilePckgInitialLoadingRef = useRef(false);
  const { useCanEditSegmentPartnerPlatformShare } = useDealContext();
  const canEditSegmentPartnerPlatformShare = useCanEditSegmentPartnerPlatformShare();
  const lastContextualPricing = useRef(contextualPricing);
  const hasContextualPricing = hasContextualFees(contextualPricing);
  // unknown while loading, or after a failure without a multi-partner audience to drive the fees
  const isContextualPricingUnknown = isLoading || (isFailed && !hasMultiProviderPricingData);
  // frozen while the contextual pricing is unknown, so the effect below still sees audience changes made meanwhile
  const prevPricingInfo = usePrevious(pricingInfo, () => !isContextualPricingUnknown);
  // declared before the effect below so the CPM lock is applied first
  const isIntersectingAudience = useIntersectingAudienceCpmLock({
    pricingInfo,
    hasFees: hasMultiProviderPricingData || hasContextualPricing,
    // targeting is not in the form values until the initial package is loaded
    loadingAudienceCheck: loadingAudienceCheck || !!pckgIsInitialLoading,
  });

  const { disableRadio, infoMessage } = useMemo(() => {
    if (isIntersectingAudience) {
      return {
        disableRadio: true,
        infoMessage:
          'Audiences built by intersecting segments from multiple data partners can only use the CPM payment method.',
      };
    }

    if (!(hasMultiProviderPricingData || hasContextualPricing) || loadingAudienceCheck || isContextualPricingUnknown) {
      return {
        disableRadio: true,
        infoMessage: '',
        shouldRequireSelection: false,
      };
    }

    return {
      disableRadio: false,
      infoMessage: null,
    };
  }, [
    isIntersectingAudience,
    hasMultiProviderPricingData,
    hasContextualPricing,
    loadingAudienceCheck,
    isContextualPricingUnknown,
  ]);

  useEffect(() => {
    /** Targeting is not in the form values until the initial package is loaded */
    if (pckgIsInitialLoading) {
      skippedWhilePckgInitialLoadingRef.current = true;
      return;
    }

    // an unknown pricing is empty: it must neither clear the fees nor become the next comparison's reference
    if (isContextualPricingUnknown) {
      return;
    }

    const isFirstRunAfterPckgInitialLoading = skippedWhilePckgInitialLoadingRef.current;
    skippedWhilePckgInitialLoadingRef.current = false;

    /** Without pricing data there is nothing to replay - keep the saved fees of an existing deal */
    const shouldReplaySkippedUpdate = isFirstRunAfterPckgInitialLoading && !!pricingInfo;

    const contextualPricingChanged = !isEqual(lastContextualPricing.current, contextualPricing);
    lastContextualPricing.current = contextualPricing;

    // a saved deal with contextual fees but no revenue method (e.g. saved before contextual fees) gets the default too
    const needsContextualDefault =
      hasContextualPricing &&
      !hasMultiProviderPricingData &&
      !loadingAudienceCheck &&
      !getValues(dealFormField.SEGMENT_PARTNER_REVENUE_METHOD);

    if (
      dealUid &&
      !shouldReplaySkippedUpdate &&
      !contextualPricingChanged &&
      isEqual(pricingInfo, prevPricingInfo) &&
      !needsContextualDefault
    ) {
      return;
    }

    if (pricingInfo?.operator === MatchingOperator.INTERSECTION) {
      setValue(dealFormField.SEGMENT_PARTNER_REVENUE_METHOD, SegmentPartnerRevenueMethod.Cpm);
    }

    const currentValue = getValues(dealFormField.SEGMENT_PARTNER_REVENUE_METHOD);
    const packageUid = getValues(dealFormField.PACKAGE_UID);
    const audienceObj = getValues('targeting.targeting.audience.openaudience_custom' as const);

    // Clear revenue method and platform share when switching to unique targeting
    if (!packageUid && !audienceObj?.val && !hasContextualPricing) {
      setValue(dealFormField.SEGMENT_PARTNER_REVENUE_METHOD, null);

      if (canEditSegmentPartnerPlatformShare) {
        setValue(dealFormField.SEGMENT_PARTNER_PLATFORM_SHARE, null);
      }
      trigger('third_party_fees_config.0.gross_share');
      return;
    }

    if (loadingAudienceCheck) {
      return;
    }

    if (hasMultiProviderPricingData) {
      if (pricingInfo?.operator === MatchingOperator.INTERSECTION) {
        if (currentValue !== SegmentPartnerRevenueMethod.Cpm) {
          setValue(dealFormField.SEGMENT_PARTNER_REVENUE_METHOD, SegmentPartnerRevenueMethod.Cpm);
        }
        return;
      }

      if (pricingInfo?.operator === MatchingOperator.UNION) {
        if (!currentValue) {
          setValue(dealFormField.SEGMENT_PARTNER_REVENUE_METHOD, SegmentPartnerRevenueMethod.PoM);
        }
        return;
      }
    }

    // contextual fees without a multi-partner audience: contextual operator is always UNION, so PoM by default
    if (!hasMultiProviderPricingData && hasContextualPricing) {
      if (!currentValue) {
        setValue(dealFormField.SEGMENT_PARTNER_REVENUE_METHOD, SegmentPartnerRevenueMethod.PoM);
      }

      // contextual rows have no platform fee, so shares left by a removed audience are cleared
      if (
        !CONTEXTUAL_PLATFORM_FEE_SUPPORTED &&
        canEditSegmentPartnerPlatformShare &&
        getValues(dealFormField.SEGMENT_PARTNER_PLATFORM_SHARE)?.length
      ) {
        setValue(dealFormField.SEGMENT_PARTNER_PLATFORM_SHARE, null);
      }
      return;
    }

    if (
      !hasMultiProviderPricingData &&
      (currentValue === SegmentPartnerRevenueMethod.Cpm || currentValue === SegmentPartnerRevenueMethod.PoM)
    ) {
      setValue(dealFormField.SEGMENT_PARTNER_REVENUE_METHOD, null);

      if (canEditSegmentPartnerPlatformShare) {
        setValue(dealFormField.SEGMENT_PARTNER_PLATFORM_SHARE, null);
      }
      trigger('third_party_fees_config.0.gross_share');
    }
  }, [
    pricingInfo,
    contextualPricing,
    hasContextualPricing,
    isContextualPricingUnknown,
    setValue,
    hasMultiProviderPricingData,
    getValues,
    loadingAudienceCheck,
    dealUid,
    prevPricingInfo,
    pckgIsInitialLoading,
    trigger,
    canEditSegmentPartnerPlatformShare,
  ]);

  return {
    disableRadio,
    infoMessage,
  };
};
